import React from 'react';
import ImageUpload from '../common/ImageUpload';
import VoiceInput from '../common/VoiceInput';
import AccordionSection from '../common/AccordionSection';

export default function MechanicChecklist({ permit, checklist, onUpdate, isReadOnly, canUpload, isOpen, onToggle, previewMode = false, onExecutionUpload }) {

    if ((!permit.checks || permit.checks.length === 0) && (!permit.pbms || permit.pbms.length === 0)) return null;

    const handleCheck = (id) => {
        if (!isReadOnly) {
            onUpdate({ ...checklist, [id]: !checklist[id] });
        }
    };

    const checks = permit.checks || [];
    const allDone = checks.length === 0 || checks.every((_, idx) => checklist[`mechanic_check_${idx}`]);

    return (
        <AccordionSection
            title="TAAK UITVOERING & CONTROLES"
            icon="🛠️"
            isOpen={isOpen}
            isComplete={allDone}
            onClick={onToggle}
        >
            <div className="space-y-6 mt-4">
                {/* PPE List - Informational */}
                {permit.pbms && permit.pbms.length > 0 && (
                    <div className="bg-slate-800/80 border border-slate-700 shadow-xl rounded-xl p-5 ring-1 ring-white/5">
                        <div className="text-[10px] text-slate-500 uppercase font-black tracking-widest mb-3 pl-1">Verplichte PBM's</div>
                        <div className="flex flex-wrap gap-2.5">
                            {permit.pbms.map((pbm, idx) => (
                                <div key={idx} className="bg-blue-500/10 border border-blue-500/30 rounded-lg px-3 py-1.5 text-xs text-blue-400 font-black uppercase tracking-wider shadow-sm">
                                    {pbm.PBM}
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Mechanic Checks */}
                {checks.length > 0 && (
                    <div className="space-y-3">
                        {checks.map((check, idx) => {
                            const id = `mechanic_check_${idx}`;
                            const isPhotoCheck = (check?.toLowerCase() || '').includes('maak een foto');
                            const photoKey = `mechanic_photo_${idx}`;
                            const voiceKey = `mechanic_voice_${idx}`;
                            const photoUrl = checklist[photoKey];
                            const isChecked = !!checklist[id];

                            if (isPhotoCheck) {
                                return (
                                    <div key={idx} className={`p-4 rounded-xl border-2 transition-all shadow-lg ${isChecked ? 'bg-blue-900/10 border-blue-500/30' : 'bg-slate-800/50 border-slate-700'
                                        }`}>
                                        <div className="flex items-start gap-4 mb-4">
                                            <div className={`w-8 h-8 rounded-xl flex items-center justify-center shrink-0 mt-1 transition-all border-2 ${isChecked
                                                ? 'bg-blue-500 border-blue-500 text-black shadow-[0_0_15px_rgba(96,165,250,0.3)]'
                                                : 'bg-slate-950 border-slate-600 text-blue-400'
                                                }`}>
                                                {isChecked ? (
                                                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={4} d="M5 13l4 4L19 7" />
                                                    </svg>
                                                ) : (
                                                    <span className="text-sm font-black">📸</span>
                                                )}
                                            </div>
                                            <div className="flex-1">
                                                <div className={`font-bold text-sm ${isChecked ? 'text-blue-400' : 'text-slate-200'}`}>{check}</div>
                                                <div className={`text-[10px] font-black uppercase tracking-widest mt-1 ${isChecked ? 'text-green-500' : 'text-blue-500 animate-pulse'}`}>
                                                    {isChecked ? '✓ Foto Toegevoegd' : 'Foto Verplicht'}
                                                </div>
                                            </div>
                                        </div>

                                        <div className="space-y-4">
                                            <ImageUpload
                                                path="execution_photos"
                                                selectedUrls={photoUrl ? [photoUrl] : []}
                                                sessionUploadedUrls={photoUrl ? [photoUrl] : []}
                                                allowDelete={true}
                                                onUploadComplete={(urls) => {
                                                    if (urls && urls.length > 0) {
                                                        const url = urls[urls.length - 1];
                                                        onUpdate({ ...checklist, [photoKey]: url, [id]: true });
                                                        // Track for session cleanup
                                                        if (onExecutionUpload) onExecutionUpload(url);
                                                    }
                                                }}
                                                onToggleSelect={() => onUpdate({ ...checklist, [photoKey]: null, [id]: false })}
                                                onSessionDelete={() => onUpdate({ ...checklist, [photoKey]: null, [id]: false })}
                                                previewMode={previewMode}
                                                compact={true}
                                                label="📸 Klik voor Foto"
                                                className="w-full"
                                                btnClassName="py-3 bg-blue-600 hover:bg-blue-500 text-black border-none shadow-lg active:scale-95 transition-all uppercase font-black tracking-widest"
                                            />

                                            <VoiceInput
                                                value={checklist[voiceKey] || ''}
                                                onChange={(e) => onUpdate({ ...checklist, [voiceKey]: e.target.value })}
                                                placeholder="Stickers missen, nummering onjuist, of procedure wijkt af..."
                                                className="w-full bg-white border-2 border-slate-300 rounded-lg p-3 text-sm text-black font-medium focus:border-red-500 outline-none transition-all placeholder:text-slate-400 ring-4 ring-black/5"
                                                minHeight="h-16"
                                                label="⚠️ Melding afwijking (optioneel)"
                                            />
                                        </div>
                                    </div>
                                );
                            }

                            return (
                                <div key={idx} className="flex gap-2">
                                    <label className={`flex-1 flex items-start gap-4 p-4 md:p-5 rounded-xl border-2 transition-all shadow-lg cursor-pointer ${isChecked ? 'bg-blue-500/10 border-blue-500/30' : 'bg-slate-800/50 border-slate-700 hover:border-slate-600 hover:bg-slate-800'
                                        }`}>
                                        <input
                                            type="checkbox"
                                            className="w-6 h-6 rounded border-slate-500 text-blue-600 bg-slate-900 mt-1 cursor-pointer shrink-0"
                                            checked={isChecked}
                                            disabled={isReadOnly}
                                            onChange={() => handleCheck(id)}
                                        />
                                        <div className="flex-1">
                                            <div className={`font-bold text-sm leading-snug ${isChecked ? 'text-blue-400' : 'text-slate-200'}`}>{check}</div>
                                            <div className="text-[10px] text-slate-500 uppercase tracking-widest font-black mt-1">Standaard Check</div>
                                        </div>
                                    </label>

                                    {/* CONTEXTUAL DOCUMENTATION ICON */}
                                    <div className="flex gap-2">
                                        {(permit.sopLink || (permit.sopLinks && permit.sopLinks.length > 0)) && (
                                            <a
                                                href={permit.sopLink || permit.sopLinks[0]?.url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="w-12 h-12 flex items-center justify-center rounded-xl bg-slate-800 border-2 border-slate-700 hover:border-blue-500/50 text-xl transition-all shadow-lg"
                                                title="Bekijk SOP"
                                            >
                                                📄
                                            </a>
                                        )}
                                        {permit.electroDocLink && (
                                            <a
                                                href={permit.electroDocLink}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="w-12 h-12 flex items-center justify-center rounded-xl bg-slate-800 border-2 border-slate-700 hover:border-yellow-500/50 text-xl transition-all shadow-lg"
                                                title="Bekijk Elektro Tekeningen"
                                            >
                                                ⚡
                                            </a>
                                        )}
                                        {permit.technischDocLink && (
                                            <a
                                                href={permit.technischDocLink}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="w-12 h-12 flex items-center justify-center rounded-xl bg-slate-800 border-2 border-slate-700 hover:border-sky-500/50 text-xl transition-all shadow-lg"
                                                title="Bekijk Technische Tekeningen"
                                            >
                                                ⚙️
                                            </a>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </AccordionSection>
    );
}
